import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import Setup2FAPage from './pages/Setup2FAPage'
import DashboardPage from './pages/DashboardPage'
import ConfigPage from './pages/ConfigPage'
import ProfilePage from './pages/ProfilePage'
import PricingPage from './pages/PricingPage'
import UnifiedCheckoutPage from './pages/UnifiedCheckoutPage'
import BillingSuccessPage from './pages/BillingSuccessPage'
import LogsPage from './pages/LogsPage'
import MCPLogsPage from './pages/MCPLogsPage'
import ModelsPage from './pages/ModelsPage'
import KnowledgesPage from './pages/KnowledgesPage'
import SyncPage from './pages/SyncPage'
import IntegrationsPage from './pages/IntegrationsPage'
import ApiKeyPage from './pages/integrations/ApiKeyPage'
import WordPressPage from './pages/integrations/WordPressPage'
import IFramePage from './pages/integrations/IFramePage'
import AgentsPage from './pages/AgentsPage'
import GlobalConfigPage from './pages/GlobalConfigPage'
import MonitoringPage from './pages/MonitoringPage'
import MCPIntegrations from './pages/MCPIntegrations'
import ConversationsPage from './pages/ConversationsPage'
import EmailConnectionsPage from './pages/EmailConnectionsPage'
import WorkflowsPage from './pages/WorkflowsPage'
import WorkflowEditorPage from './pages/workflows/WorkflowEditorPage'
import WorkflowDetailPage from './pages/workflows/WorkflowDetailPage'
import PlansAdminPage from './pages/PlansAdminPage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import AdminMetricsPage from './pages/AdminMetricsPage'
import AdminUsersPage from './pages/AdminUsersPage'
import ErrorBoundary from './components/ErrorBoundary'
import ImpersonationBanner from './components/ImpersonationBanner'
import {
  AccountPage,
  AccountOverview,
  AccountSubscription,
  AccountBilling,
  AccountUsage,
  AccountSettings,
} from './pages/account'
import ChatWidget from './components/ChatWidget'
import ContactPage from './pages/ContactPage'
import DocumentationPage from './pages/DocumentationPage'
import LandingPage from './pages/landing/LandingPage'
import NotFoundPage from './pages/NotFoundPage'
import AppLayout from './components/layout/AppLayout'
import { OnboardingWidget, DiscoveryTooltip } from './components/onboarding'
import { getApiBase, getToken, setThemeClass } from './lib/utils'
import { isImpersonationToken } from './lib/jwt'

export default function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const [token, setToken] = useState<string | null>(getToken())
  const [user, setUser] = useState<any>(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null') } catch { return null }
  })
  const [impersonationActive, setImpersonationActive] = useState<boolean>(() => {
    // Vérifier à la fois le flag localStorage ET le token JWT
    const flag = localStorage.getItem('impersonation_active') === 'true'
    const token = getToken()
    return flag && isImpersonationToken(token)
  })
  const [impersonationTargetEmail, setImpersonationTargetEmail] = useState<string | null>(() => {
    return localStorage.getItem('impersonation_target_email')
  })
  const changelogRaw = useMemo(() => {
    const modules = import.meta.glob('../CHANGELOG.md', { as: 'raw', eager: true }) as Record<string, string>
    const first = Object.values(modules)[0]
    return typeof first === 'string' ? first : ''
  }, [])
  const [appVersion, setAppVersion] = useState<string>('')
  const [brandingLogo, setBrandingLogo] = useState<string | null>(() => {
    try { return localStorage.getItem('branding_logo_url') || null } catch { return null }
  })
  const apiBase = useMemo(() => getApiBase().replace(/\/$/, ''), [])
  const changelogVersion = useMemo(() => extractLatestVersionFromChangelog(changelogRaw), [changelogRaw])
  const displayedVersion = appVersion || changelogVersion || '1.0.0'

  useEffect(() => {
    setThemeClass()
  }, [])

  useEffect(() => {
    document.title = 'Agent IA by WpTotem'
  }, [])

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      try {
        const res = await fetch(`${getApiBase()}/health`)
        if (!res.ok) throw new Error('health check failed')
        const data = await res.json()
        if (data?.version && !cancelled) {
          setAppVersion(String(data.version))
          return
        }
        throw new Error('no version in payload')
      } catch {
        if (!cancelled) {
          setAppVersion(changelogVersion || '1.0.0')
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [changelogVersion])

  useEffect(() => {
    const handle = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        setToken(getToken())
      } else if (e.key === 'user') {
        try { setUser(JSON.parse(e.newValue || 'null')) } catch { setUser(null) }
      } else if (e.key === 'branding_logo_url') {
        setBrandingLogo(e.newValue || null)
      } else if (e.key === 'impersonation_active' || e.key === 'access_token') {
        // Vérifier à la fois le flag localStorage ET le token JWT
        const flag = localStorage.getItem('impersonation_active') === 'true'
        const token = getToken()
        setImpersonationActive(flag && isImpersonationToken(token))
      } else if (e.key === 'impersonation_target_email') {
        setImpersonationTargetEmail(e.newValue)
      }
    }
    window.addEventListener('storage', handle)
    return () => window.removeEventListener('storage', handle)
  }, [])

  const authed = !!token
  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('user') || 'null')
      setUser(parsed)
    } catch {
      setUser(null)
    }
  }, [token])
  const isAdmin = !!user?.is_superuser
  const firstName = useMemo(() => {
    if (user?.first_name) return user.first_name
    if (!user?.email) return 'Profil'
    const local = String(user.email).split('@')[0]
    const parts = local.replace(/\d+/g, '').split(/[._-]+/).filter(Boolean)
    const name = (parts[0] || local)
    return name.charAt(0).toUpperCase() + name.slice(1)
  }, [user])
  const updateBrandingLogo = useCallback((url: string | null) => {
    setBrandingLogo(url)
    try {
      if (url) {
        localStorage.setItem('branding_logo_url', url)
      } else {
        localStorage.removeItem('branding_logo_url')
      }
    } catch {}
    updateFaviconLinks(url)
  }, [])

  const refreshBrandingLogo = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/branding/config`)
      if (!response.ok) return
      const data = await response.json()
      const rawUrl = data?.logo_url as string | null | undefined
      if (!rawUrl) {
        updateBrandingLogo(null)
        return
      }
      const normalized = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`
      updateBrandingLogo(`${apiBase}${normalized}`)
    } catch {
      // noop
    }
  }, [apiBase, updateBrandingLogo])

  useEffect(() => {
    refreshBrandingLogo()
  }, [refreshBrandingLogo])

  useEffect(() => {
    updateFaviconLinks(brandingLogo)
  }, [brandingLogo])

  const pageTitle = useMemo(() => {
    const path = location.pathname
    if (path === '/') return 'Tableau de bord'
    if (path === '/conversations') return 'Conversations'
    if (path === '/agents') return 'Agents IA'
    if (path === '/knowledges') return 'Base de connaissances'
    if (path === '/media' || path === '/contents') return 'Bibliothèque'
    if (path === '/subjects') return 'Catalogue'
    if (path === '/global-config') return 'Administration'
    if (path === '/config') return 'Configuration'
    if (path === '/users') return 'Utilisateurs'
    if (path === '/models') return 'Modèles'
    if (path === '/integrations') return 'Intégrations'
    if (path === '/integrations/mcp') return 'MCP'
    if (path === '/monitoring') return 'Monitoring'
    if (path === '/logs') return 'Logs'
    if (path === '/logs-mcp') return 'Logs MCP'
    if (path === '/profile') return 'Profil'
    if (path === '/setup-2fa') return 'Authentification 2FA'
    if (path === '/workflows') return 'Workflows'
    if (path.startsWith('/workflows/')) return 'Workflows'
    if (path === '/admin/plans') return 'Gestion des Plans'
    if (path === '/admin/users') return 'Utilisateurs Admin'
    if (path === '/admin') return 'Administration'
    if (path === '/pricing') return 'Tarifs'
    if (path.startsWith('/account')) return 'Mon Compte'
    return undefined
  }, [location.pathname])

  return (
    <>
      {/* Banner impersonation (si actif) */}
      {authed && impersonationActive && impersonationTargetEmail && (
        <ImpersonationBanner
          adminEmail={user?.email || 'Admin'}
          targetEmail={impersonationTargetEmail}
        />
      )}

      {authed ? (
        <div className={impersonationActive ? 'pt-16' : ''}>
          <AppLayout 
          firstName={firstName} 
          onLogout={() => {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
            navigate('/login');
          }}
          version={displayedVersion}
          pageTitle={pageTitle}
        >
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            {/* Redirections anciennes URLs vers /knowledges */}
            <Route path="/contents" element={<Navigate to="/knowledges" replace />} />
            <Route path="/media" element={<Navigate to="/knowledges" replace />} />
            <Route path="/subjects" element={<Navigate to="/knowledges" replace />} />
            <Route path="/knowledges" element={<KnowledgesPage />} />
            <Route path="/knowledges/sync" element={<SyncPage />} />
            <Route path="/config" element={<ConfigPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/integrations/api-key" element={<ApiKeyPage />} />
            <Route path="/integrations/wordpress" element={<WordPressPage />} />
            <Route path="/integrations/iframe" element={<IFramePage />} />
            <Route path="/integrations/mcp" element={<MCPIntegrations />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/conversations" element={<ConversationsPage />} />
            <Route path="/email" element={<EmailConnectionsPage />} />
            <Route path="/workflows" element={<WorkflowsPage />} />
            <Route path="/workflows/new" element={<WorkflowEditorPage />} />
            <Route path="/workflows/:id" element={<WorkflowDetailPage />} />
            <Route path="/workflows/:id/edit" element={<WorkflowEditorPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/documentation" element={<DocumentationPage />} />
            
            {/* Account Routes */}
            <Route path="/account" element={<AccountPage />}>
              <Route index element={<Navigate to="/account/overview" replace />} />
              <Route path="overview" element={<AccountOverview />} />
              <Route path="subscription" element={<AccountSubscription />} />
              <Route path="billing" element={<AccountBilling />} />
              <Route path="usage" element={<AccountUsage />} />
              <Route path="settings" element={<AccountSettings />} />
            </Route>
            <Route path="/setup-2fa" element={<Setup2FAPage />} />
            
            {/* Admin Routes */}
            <Route
              path="/global-config"
              element={isAdmin ? <GlobalConfigPage onBrandingUpdate={updateBrandingLogo} /> : <Navigate to="/" replace />}
            />
            <Route path="/logs" element={isAdmin ? <LogsPage /> : <Navigate to="/" replace />} />
            <Route path="/logs-mcp" element={isAdmin ? <MCPLogsPage /> : <Navigate to="/" replace />} />
            <Route path="/models" element={isAdmin ? <ModelsPage /> : <Navigate to="/" replace />} />
            <Route path="/monitoring" element={isAdmin ? <MonitoringPage /> : <Navigate to="/" replace />} />
            <Route path="/admin" element={isAdmin ? <AdminDashboardPage /> : <Navigate to="/" replace />} />
            <Route path="/admin/plans" element={isAdmin ? <PlansAdminPage /> : <Navigate to="/" replace />} />
            <Route path="/admin/users" element={isAdmin ? <AdminUsersPage /> : <Navigate to="/" replace />} />
            <Route 
              path="/admin/metrics" 
              element={
                isAdmin ? (
                  <ErrorBoundary>
                    <AdminMetricsPage />
                  </ErrorBoundary>
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />

            {/* Fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppLayout>
        </div>
      ) : (
        <div className="min-h-screen flex flex-col">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage onLogin={() => setToken(getToken())} />} />
            <Route path="/register" element={<RegisterPage onLogin={() => setToken(getToken())} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pricing" element={<Navigate to="/login" />} />
            <Route path="/checkout" element={<UnifiedCheckoutPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      )}

{/* Widget chat - visible en mode authentifié (sauf certaines pages) et en mode public */}
      {((authed &&
        !location.pathname.startsWith('/workflows') &&
        location.pathname !== '/pricing' &&
        !location.pathname.startsWith('/account') &&
        !location.pathname.startsWith('/agents')) ||
        !authed) &&
        <ChatWidget />}
      
      {/* Onboarding Widget - visible sur toutes les pages authentifiées */}
      {authed && <OnboardingWidget />}
      {authed && <DiscoveryTooltip />}
    </>
  )
}

function extractLatestVersionFromChangelog(raw: string) {
  if (!raw) return null
  
  // Find all versions in the changelog
  const matches = raw.matchAll(/^## \[(\d+\.\d+\.\d+(?:[^\]]*)?)\]/gm)
  const versions = Array.from(matches, m => m[1].split(' ')[0])
  
  if (versions.length === 0) return null
  if (versions.length === 1) return versions[0]
  
  // Compare versions as semver (major.minor.patch)
  const compareVersions = (a: string, b: string): number => {
    const aParts = a.split('.').map(Number)
    const bParts = b.split('.').map(Number)
    
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aNum = aParts[i] || 0
      const bNum = bParts[i] || 0
      if (aNum > bNum) return 1
      if (aNum < bNum) return -1
    }
    return 0
  }
  
  // Return the highest version
  return versions.reduce((latest, current) => 
    compareVersions(current, latest) > 0 ? current : latest
  )
}

function updateFaviconLinks(url: string | null) {
  const href = url || '/branding/logo.png'
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  const apple = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
  if (link) link.href = href
  if (apple) apple.href = href
}
